import { useEffect, useMemo, useCallback } from 'react';

import useConsumerStore from '../../store/consumer.store';

import { useConsumerViewParams } from '../useConsumerViewParams';
import { useConsumerUrls } from '../useConsumerUrls';
import { useConsumerData } from '../useConsumerData';
import { useConsumerErrorHandling } from '../useConsumerErrorHandling.tsx';
import { useConsumerExport } from '../useConsumerExport';

export const useConsumerView = () => {
  const { shouldLoadData, isLoading, setIsLoading, setShouldLoadData, queryCounter } = useConsumerStore();

  const params = useConsumerViewParams();
  const urls = useConsumerUrls(params);
  const cacheKey = useMemo(() => (params ? `${JSON.stringify(params)}-${queryCounter}` : null), [params, queryCounter]);

  const {
    tableDataQuery,
    chartDataQuery,
    isLoading: queriesLoading,
    isError,
    isChartFetching,
  } = useConsumerData(urls, cacheKey, shouldLoadData);

  const { isExporting, exportData } = useConsumerExport(urls);

  useConsumerErrorHandling(tableDataQuery.error, chartDataQuery.error, chartDataQuery.data);

  useEffect(() => {
    if (!shouldLoadData) {
      setIsLoading(false);
      return;
    }

    if (queriesLoading) {
      setIsLoading(true);
      return;
    }

    if (!queriesLoading) {
      setIsLoading(false);
      setShouldLoadData(false);
    }
  }, [shouldLoadData, queriesLoading, setIsLoading, setShouldLoadData]);

  const loadData = useCallback(() => {
    if (!params) return;
    useConsumerStore.getState().incrementQueryCounter();
    setIsLoading(true);
    setShouldLoadData(true);
  }, [params, setIsLoading, setShouldLoadData]);

  return {
    tableData: tableDataQuery.data,
    chartData: chartDataQuery.data,
    mutateChart: chartDataQuery.refetch,
    mutateTable: tableDataQuery.refetch,
    isLoading,
    isError,
    loadData,
    canLoadData: Boolean(params),
    isExporting,
    exportData,
    isChartFetching,
  };
};
